/**
 * QAICoachPage (F4) - v6.0 3-Column Grid Layout
 * Main Q AI Coach interface for standalone users
 *
 * Route: /q-ai/coach (protected - requires Q AI authentication)
 * Target: Retail MT5 traders who signed up via Q AI standalone
 *
 * v6.0 Updates (PHASE 2.1 - 3-Column Grid):
 * - Desktop (1280px+): 3-column CSS Grid [180px | 1fr | 280px]
 * - LEFT: QSignalList watchlist (fixed 180px)
 * - CENTER: QSignalBoard DNA Matrix (flexible 1fr)
 * - RIGHT: QChatWindow (fixed 280px)
 * - Mobile/Tablet: Single column with FAB toggle
 *
 * v5.0 Updates (Phase 5):
 * - EA Assistant tab ("My EAs") for analyzing and modifying MQL5 files
 * - Tab navigation between Chat and My EAs
 * - Integration with QBridge for file scanning
 *
 * v4.0 Updates (Task 2.3/3.3):
 * - Pass hasConversation for continuity indicator
 * - Pass progressState and warnings for loading UI
 * - Response source tracking
 */

import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Chip, Typography, Fab, useMediaQuery, useTheme, Tabs, Tab, Badge } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import useQChat from '../../hooks/useQChat';
import useQuantXData from '../../hooks/useQuantXData';
import { useStrategyDNA } from '../../hooks/useStrategyDNA';
import QChatWindow from '../../Components/QAI/QChatWindow';
// QAnalysisBoard replaced by QSignalBoard (Phase 3)
import QSignalBoard from '../../Components/QAI/QSignalBoard';
import QPnLSummary from '../../Components/QAI/QPnLSummary';
import QSignalTable from '../../Components/QAI/QSignalTable';
// Phase 1 MT5-style polish - Grid view components
import QSignalGrid from '../../Components/QAI/QSignalGrid';
// PHASE 2.1: Watchlist component for 3-column layout
import QSignalList from '../../Components/QAI/QSignalList';
import PricingModal from '../../Components/QAI/PricingModal';
import QAILogo from '../../Components/QAI/QAILogo';
import TokenBalance from '../../Components/QAI/TokenBalance';
import QBridgeSetupModal, { shouldShowQBridgeSetup } from '../../Components/QAI/QBridgeSetupModal';
import QBridgeSetupWizardV2 from '../../Components/QAI/QBridgeSetupWizardV2';
import QBridgeHelpModal from '../../Components/QAI/QBridgeHelpModal';
import QAILoadingProgress from '../../Components/QAI/QAILoadingProgress';
import { EAManagerTab } from '../../Components/QAI/EAAssistant';
import { FEATURE_FLAGS } from '../../config/featureFlags';
import { pairToQuantX, getQuantXSymbols } from '../../utils/symbolMapper';
// QBridgeStatusBanner reserved for future inline status display
import { getSyncStatus, getSignalBoard } from '../../api/qaiApi';
import { checkQBridgeStatus, detectMT5, QBRIDGE_STATUS } from '../../services/qbridge.service';

/**
 * Default QuantX symbols for dropdown when no signals are available
 * These are ALL symbols supported by QuantX API (matches Market Watch)
 *
 * AUDIT: Must match QTradeView Market Watch symbols exactly
 */
const DEFAULT_QUANTX_SYMBOLS = [
  'XAUUSD_ct',   // Gold USD
  'XAUEUR_ct',   // Gold EUR
  'XAGUSD_ct',   // Silver USD
  'XAGEUR_ct',   // Silver EUR
  'EURUSD_ct',   // Euro/USD
  'GBPUSD_ct',   // Pound/USD
  'BTCUSD_ct',   // Bitcoin
  'ETHUSD_ct'    // Ethereum
];

/**
 * QAICoachPage Component
 * Full-screen Q AI chat interface for standalone users
 *
 * v5.0 Updates (Feature Flagged Layout):
 * - COLLAPSIBLE_DASHBOARD: Desktop dashboard can collapse for more chat space
 * - MOBILE_RESPONSIVE: Full-screen mobile experience with FAB toggle
 */
const QAICoachPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // DEBUG: Log mobile layout state
  useEffect(() => {
    console.log('[QAICoachPage] Mobile Layout Debug:', {
      isMobile,
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
      documentHeight: document.documentElement.clientHeight,
      visualViewportHeight: window.visualViewport?.height,
      breakpoint: theme.breakpoints.values,
      MOBILE_RESPONSIVE: FEATURE_FLAGS.MOBILE_RESPONSIVE
    });
  }, [isMobile, theme.breakpoints.values]);

  // Layout state - collapsible dashboard (desktop) and view toggle (mobile)
  const [isDashboardCollapsed, setIsDashboardCollapsed] = useState(false);
  const [mobileView, setMobileView] = useState('chat'); // 'chat' | 'dashboard'

  // Tab state - Chat vs My EAs (Phase 5)
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'my-eas'

  // Phase 1: Signal board view mode (grid vs table)
  // Phase 2: Default to 'grid' for enhanced institutional UI
  const [signalViewMode, setSignalViewMode] = useState('grid'); // 'grid' | 'table'

  // Phase 1B: Selected symbol for detailed view (Enhanced Signal Grid)
  const [selectedSymbol, setSelectedSymbol] = useState('XAUUSD_ct');

  // Get Q AI token and user from localStorage - use state with effect to handle navigation timing
  const [qaiToken, setQaiToken] = useState(null);
  const [qaiUser, setQaiUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Read token from localStorage on mount and after any navigation
  useEffect(() => {
    const storedToken = localStorage.getItem('qai_token') || localStorage.getItem('token');
    const userStr = localStorage.getItem('qai_user') || localStorage.getItem('user');

    console.log('Coach page - qai_token:', storedToken ? storedToken.substring(0, 20) + '...' : 'NOT FOUND');

    if (storedToken) {
      setQaiToken(storedToken);
      try {
        setQaiUser(userStr ? JSON.parse(userStr) : null);
      } catch {
        setQaiUser(null);
      }
    }
    setIsInitialized(true);
  }, []);

  const userName = qaiUser?.firstName || qaiUser?.name || 'Trader';

  // QBridge connection status (real-time localhost check + backend sync status)
  const [qbridgeStatus, setQbridgeStatus] = useState({
    connected: false,       // Real-time QBridge+MT5 status from localhost
    qbridgeRunning: false,  // QBridge app running on localhost:5556
    mt5Detected: false,     // MT5 detected by QBridge
    mt5Account: null,       // MT5 account number
    mt5Broker: null,        // MT5 broker name
    isFallbackDetection: false, // True if detected via EA file list (no account info)
    hasSynced: false,       // Backend sync status (has user ever synced)
    lastSync: null,
    tradeCount: 0
  });

  // QBridge modal states
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showQBridgeWizard, setShowQBridgeWizard] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Analysis Board data state
  const [analysisData, setAnalysisData] = useState(null);
  const [isAnalysisLive, setIsAnalysisLive] = useState(false);
  const [lastAnalysisUpdate, setLastAnalysisUpdate] = useState(null);

  // Signal Board data state (from GET /api/q/board)
  const [signalBoardData, setSignalBoardData] = useState({
    signals: [],
    updatedAt: null,
    signalCount: 0
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // SYMBOL WIRING FIX: Unified symbol management for all components
  // - Signal Board pairs (GOLD/SILVER) map to QuantX (_ct) for API calls
  // - No duplicates in dropdown (pair and _ct pointing to same instrument)
  // - All symbols wired to indicators, Strategy DNA, and Q.AI chat
  // ─────────────────────────────────────────────────────────────────────────────

  // Get pair symbols directly from signal board (for dropdown display)
  const availablePairSymbols = useMemo(() => {
    if (!signalBoardData.signals?.length) return [];
    return signalBoardData.signals.map(s => s.symbol);
  }, [signalBoardData.signals]);

  // Compute which QuantX symbols are already represented by signal board pairs
  const mappedQuantXFromPairs = useMemo(() => {
    return new Set(availablePairSymbols.map(p => pairToQuantX(p)).filter(Boolean));
  }, [availablePairSymbols]);

  // Compute QuantX symbols from pair symbols (for API calls)
  const availableQuantXSymbols = useMemo(() => {
    if (!signalBoardData.signals?.length) return DEFAULT_QUANTX_SYMBOLS;
    const pairSymbols = signalBoardData.signals.map(s => s.symbol);
    const converted = getQuantXSymbols(pairSymbols);
    // Always include defaults and merge with converted symbols (unique)
    const merged = [...new Set([...converted, ...DEFAULT_QUANTX_SYMBOLS])];
    return merged.length > 0 ? merged : DEFAULT_QUANTX_SYMBOLS;
  }, [signalBoardData.signals]);

  // FIXED v2: Dropdown shows BOTH cross-pairs AND all QuantX symbols
  // Market Watch has 8 symbols, Signal Board has cross-pairs - show ALL
  const dropdownSymbols = useMemo(() => {
    // Section 1: Cross-pairs from Signal Board (GOLD/SILVER, XAUUSD/XAUEUR, etc.)
    const crossPairs = availablePairSymbols;

    // Section 2: ALL 8 QuantX symbols from Market Watch (always show these)
    // This ensures XAUUSD_ct, XAUEUR_ct, etc. are always available
    const allQuantXSymbols = DEFAULT_QUANTX_SYMBOLS;

    // Combine: Cross-pairs first, then QuantX symbols
    // Use Set to avoid duplicates if a symbol appears in both
    const combined = [...crossPairs];
    allQuantXSymbols.forEach(sym => {
      if (!combined.includes(sym)) {
        combined.push(sym);
      }
    });

    // Log for debugging
    console.log('[QAICoachPage] Dropdown symbols v2:', {
      crossPairs,
      quantXSymbols: allQuantXSymbols,
      combined,
      total: combined.length
    });

    return combined;
  }, [availablePairSymbols]);

  // Map selected symbol to QuantX format for API calls
  // Handles: GOLD/SILVER → XAUUSD_ct, XAUUSD/XAUEUR → XAUUSD_ct, BTCUSD_ct → BTCUSD_ct
  const selectedQuantXSymbol = useMemo(() => {
    if (!selectedSymbol) return 'XAUUSD_ct';
    // If already a QuantX symbol, use as-is
    if (selectedSymbol.endsWith('_ct')) return selectedSymbol;
    // Convert pair to QuantX (handles cross-pairs like XAUUSD/XAUEUR)
    const mapped = pairToQuantX(selectedSymbol);
    console.log('[QAICoachPage] Symbol mapping:', { from: selectedSymbol, to: mapped });
    return mapped || 'XAUUSD_ct';
  }, [selectedSymbol]);

  // Auto-select first available symbol when signals load (if none selected yet)
  useEffect(() => {
    if (dropdownSymbols.length > 0 && !dropdownSymbols.includes(selectedSymbol)) {
      console.log('[QAICoachPage] Auto-selecting first symbol:', dropdownSymbols[0]);
      setSelectedSymbol(dropdownSymbols[0]);
    }
  }, [dropdownSymbols, selectedSymbol]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 1B: QuantX Data Hook for Enhanced Signal Grid
  // Provides: ADX, ATR, Strategy DNA, Fibonacci, Trend analysis per symbol
  // Phase 2B: Use selectedQuantXSymbol (mapped from pair) for API calls
  // ─────────────────────────────────────────────────────────────────────────────
  const quantXData = useQuantXData({
    symbol: selectedQuantXSymbol,  // Use mapped QuantX symbol for API
    token: qaiToken,
    enabled: FEATURE_FLAGS.ENHANCED_SIGNAL_GRID && !!selectedQuantXSymbol && !!qaiToken,
    autoRefresh: true,
    refreshInterval: 30000
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Phase 2: Strategy DNA Hook for Enhanced Signal Grid
  // Provides: Bounce/Break/Fakeout rates, MFE/MAE, Level Stats
  // Phase 2B: Use selectedQuantXSymbol (mapped from pair) for API calls
  // ─────────────────────────────────────────────────────────────────────────────
  const strategyDNAData = useStrategyDNA({
    symbol: selectedQuantXSymbol,  // Use mapped QuantX symbol for API
    token: qaiToken,
    enabled: FEATURE_FLAGS.ENHANCED_SIGNAL_GRID && !!selectedQuantXSymbol && !!qaiToken,
    autoRefresh: true,
    refreshInterval: 60000
  });

  // Merge quantXData with Strategy DNA data for QSignalGrid
  const mergedQuantXData = useMemo(() => {
    if (!quantXData) return null;
    return {
      ...quantXData,
      strategyDNA: strategyDNAData?.summary || strategyDNAData?.overallStats || null,
      strategyDNAStats: strategyDNAData?.stats || [],
      strategyDNALoading: strategyDNAData?.loading || false
    };
  }, [quantXData, strategyDNAData]);

  // Handle analysis data from AI responses
  const handleAnalysisData = useCallback((data) => {
    setAnalysisData(data);
    setIsAnalysisLive(true);
    setLastAnalysisUpdate(new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    }));

    // Turn off "live" indicator after 30 seconds
    setTimeout(() => setIsAnalysisLive(false), 30000);
  }, []);

  // Initialize Q Chat hook (Task 2.3/3.3/4.6: includes conversation, source, and history state)
  const {
    messages,
    balance,
    isLoading,
    showPricingModal,
    error,
    isLowBalance,
    canSend,
    sendMessage,
    handleActionCard,
    handleActionChip,
    refreshBalance,
    cancelStream,
    clearMessages,
    openPricingModal,
    closePricingModal,
    // Task 2.3: Conversation state
    hasConversation,
    conversationId,
    // Task 3.3: Response source (available on messages)
    lastResponseSource,
    // Task 4.6: Conversation history
    conversationHistory,
    isLoadingHistory,
    refreshConversationHistory,
    loadConversation,
    deleteConversation,
    // UI-6/8: Progress and warnings
    progressState,
    warnings
  } = useQChat(qaiToken, userName, handleAnalysisData, selectedQuantXSymbol);

  // Ref to track if balance has been fetched
  const balanceFetched = useRef(false);

  // Redirect to login if no token (only after initialization is complete)
  useEffect(() => {
    if (isInitialized && !qaiToken) {
      console.log('No token found after init, redirecting to login');
      navigate('/q-ai/login');
    }
  }, [isInitialized, qaiToken, navigate]);

  // Fetch balance when token becomes available
  useEffect(() => {
    if (qaiToken && !balanceFetched.current) {
      console.log('[QAICoachPage] Token available, fetching balance...');
      balanceFetched.current = true;
      refreshBalance().then(result => {
        if (result) {
          console.log('[QAICoachPage] Initial balance loaded:', result.balance);
        } else {
          console.warn('[QAICoachPage] Balance fetch returned null');
        }
      });
    }
  }, [qaiToken, refreshBalance]);

  // Real-time QBridge + MT5 status check from localhost
  // This shows actual connection status, not just backend sync history
  useEffect(() => {
    let intervalId = null;

    const checkQBridgeAndMT5 = async () => {
      try {
        // Check if QBridge is running on localhost:5556
        const qbridgeResult = await checkQBridgeStatus();
        const qbridgeRunning = qbridgeResult.status === QBRIDGE_STATUS.READY;

        if (qbridgeRunning) {
          // QBridge is running, now check MT5 detection
          const mt5Result = await detectMT5();
          setQbridgeStatus(prev => ({
            ...prev,
            connected: mt5Result.detected,
            qbridgeRunning: true,
            mt5Detected: mt5Result.detected,
            mt5Account: mt5Result.account,
            mt5Broker: mt5Result.broker,
            isFallbackDetection: mt5Result._fallback || false
          }));
        } else {
          // QBridge not running
          setQbridgeStatus(prev => ({
            ...prev,
            connected: false,
            qbridgeRunning: false,
            mt5Detected: false,
            mt5Account: null,
            mt5Broker: null
          }));
        }
      } catch (err) {
        // Network error - QBridge not running
        setQbridgeStatus(prev => ({
          ...prev,
          connected: false,
          qbridgeRunning: false,
          mt5Detected: false
        }));
      }
    };

    // Initial check
    checkQBridgeAndMT5();

    // Poll every 10 seconds for real-time status
    intervalId = setInterval(checkQBridgeAndMT5, 10000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Fetch backend sync status (for historical data)
  useEffect(() => {
    const checkSyncStatus = async () => {
      if (!qaiToken) return;

      try {
        const status = await getSyncStatus(qaiToken);
        setQbridgeStatus(prev => ({
          ...prev,
          hasSynced: status.hasSynced || false,
          lastSync: status.lastSyncAt || null,
          tradeCount: status.tradeCount || 0
        }));
      } catch (err) {
        console.error('Failed to fetch sync status:', err);
      }
    };

    checkSyncStatus();
  }, [qaiToken]);

  // Check if should show QBridge wizard on first visit (if not synced)
  useEffect(() => {
    if (qaiToken && !qbridgeStatus.connected && shouldShowQBridgeSetup()) {
      // Small delay to let the page render first
      const timer = setTimeout(() => {
        setShowQBridgeWizard(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [qaiToken, qbridgeStatus.connected]);

  // Fetch Signal Board data from API (GET /api/q/board)
  // Polls every 30 seconds for fresh signals
  // Use ref to prevent unnecessary re-renders from data comparison
  const signalBoardRef = useRef(null);
  const liveTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchSignalBoard = async () => {
      if (!qaiToken) return;

      try {
        const data = await getSignalBoard(qaiToken);

        // DEBUG: Log actual API response to understand signal structure
        console.log('[SignalBoard] API Response:', JSON.stringify(data.signals, null, 2));

        // Only update state if data actually changed (prevents jitter)
        const newDataStr = JSON.stringify(data.signals);
        const oldDataStr = JSON.stringify(signalBoardRef.current);

        if (newDataStr !== oldDataStr) {
          signalBoardRef.current = data.signals;
          setSignalBoardData(data);
        }

        // Update live indicator and timestamp (only update timestamp if new data)
        if (data.signals && data.signals.length > 0) {
          // Clear previous timeout to prevent multiple timeouts
          if (liveTimeoutRef.current) {
            clearTimeout(liveTimeoutRef.current);
          }

          setIsAnalysisLive(true);
          setLastAnalysisUpdate(new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
          }));

          // Turn off "live" indicator after 30 seconds
          liveTimeoutRef.current = setTimeout(() => setIsAnalysisLive(false), 30000);
        }
      } catch (err) {
        console.warn('Failed to fetch signal board:', err);
      }
    };

    // Initial fetch
    fetchSignalBoard();

    // Poll every 30 seconds
    const intervalId = setInterval(fetchSignalBoard, 30000);

    return () => {
      clearInterval(intervalId);
      if (liveTimeoutRef.current) {
        clearTimeout(liveTimeoutRef.current);
      }
    };
  }, [qaiToken]);

  // Handle logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem('qai_token');
    localStorage.removeItem('qai_user');
    navigate('/q-ai');
  }, [navigate]);

  // Handle MT5 chip click - opens QBridge wizard
  const handleMT5Click = useCallback(() => {
    if (qbridgeStatus.connected) {
      // If connected, could show status or sync options
      console.log('MT5 already connected, last sync:', qbridgeStatus.lastSync);
    } else {
      // If not connected, open new QBridge wizard
      setShowQBridgeWizard(true);
    }
  }, [qbridgeStatus.connected, qbridgeStatus.lastSync]);

  // Handle QBridge setup completion - refresh sync status and balance from API
  const handleSetupComplete = useCallback(async (importedCount) => {
    console.log('[QAICoachPage] Setup complete, imported trades:', importedCount);

    // Update local state immediately for responsive UI
    setQbridgeStatus({
      connected: true,
      lastSync: new Date().toISOString(),
      tradeCount: importedCount || 0
    });

    // Refresh sync status and balance from API
    if (qaiToken) {
      try {
        // Refresh sync status to get real data
        const status = await getSyncStatus(qaiToken);
        setQbridgeStatus({
          connected: status.hasSynced || true,
          lastSync: status.lastSyncAt || new Date().toISOString(),
          tradeCount: status.tradeCount || importedCount || 0
        });

        // Also refresh token balance - import may have used tokens or backend may have updated
        await refreshBalance();
        console.log('[QAICoachPage] Balance refreshed after import');
      } catch (err) {
        console.error('Failed to refresh sync status or balance:', err);
      }
    }
  }, [qaiToken, refreshBalance]);

  // Handle help request - sends message to chat
  const handleAskQBridgeHelp = useCallback(() => {
    // Send installation help message to chat (FREE prompt)
    sendMessage('Help me install QBridge', 'chat');
  }, [sendMessage]);

  // Handle tab change (Phase 5 - EA Assistant)
  // Auto-collapse dashboard when switching to My EAs tab for full-width layout
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    // Auto-collapse dashboard for My EAs tab to give EA Manager full width
    // Dashboard is not needed during EA editing - user can manually expand if needed
    if (FEATURE_FLAGS.COLLAPSIBLE_DASHBOARD) {
      if (newValue === 'my-eas') {
        setIsDashboardCollapsed(true);
      } else if (newValue === 'chat') {
        setIsDashboardCollapsed(false);
      }
    }
  }, []);

  // Handle "Ask Q" from EA Assistant - switches to chat tab and sends message
  const handleAskQFromEA = useCallback((message) => {
    setActiveTab('chat');
    // Small delay to ensure tab switch animation completes
    setTimeout(() => {
      sendMessage(message, 'chat');
    }, 100);
  }, [sendMessage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Transform Signal Board data for QSignalTable (multi-symbol view)
  // ─────────────────────────────────────────────────────────────────────────────

  // Map zSignal to trend score: STRONG_BUY=100, WEAK_BUY=75, NEUTRAL=50, etc.
  const zSignalToTrendScore = (zSignal) => {
    const scoreMap = {
      'STRONG_BUY': 95,
      'WEAK_BUY': 70,
      'BUY': 80,
      'NEUTRAL': 50,
      'WAIT': 50,
      'WEAK_SELL': 30,
      'SELL': 20,
      'STRONG_SELL': 5
    };
    return scoreMap[zSignal] ?? 50;
  };

  // Transform signals for QSignalTable - include ALL available fields from API
  // API provides: qSignal (HOLD/BUY/SELL), qScore (0-100), zSignal, zScore, percentile
  const signalTableData = (signalBoardData.signals || []).map((sig) => ({
    // Core signal data - qSignal is Q AI's final verdict (USE THIS)
    symbol: sig.symbol,
    qSignal: sig.qSignal,                 // Q AI verdict: "HOLD" | "BUY" | "SELL"
    qScore: sig.qScore,                   // Q AI score: 0-100
    zScore: sig.zScore,
    zSignal: sig.zSignal,
    trendScore: sig.qScore ?? zSignalToTrendScore(sig.zSignal), // Backward compat
    percentile: sig.percentile,

    // Trade levels
    entry: sig.entry,
    takeProfit: sig.takeProfit,
    stopLoss: sig.stopLoss,

    // Support/Resistance (P5/P95)
    distribution: sig.supportP5 || sig.resistanceP95 ? {
      p5: sig.supportP5,
      p25: sig.p25 || null,
      p50: sig.p50 || null,
      p75: sig.p75 || null,
      p95: sig.resistanceP95
    } : null,

    // EMA trend data (if API provides)
    ema: sig.ema || null,           // { ema20, ema50, ema100, ema200 }
    emaTrend: sig.emaTrend || null, // 'BULLISH' | 'BEARISH' | 'NEUTRAL'

    // Fibonacci levels (if API provides)
    fibLevels: sig.fibLevels || null, // { fib236, fib382, fib500, fib618, fib786 }

    // Multi-timeframe analysis
    timeframes: sig.timeframes || null,

    // Additional indicators (pass through any extra data)
    rsi: sig.rsi || null,
    macd: sig.macd || null,
    atr: sig.atr || null,
    volume: sig.volume || null,
    spread: sig.spread || null,

    // Raw API data for debugging
    _raw: sig
  }));

  // Handle buy tokens - opens pricing modal
  const handleBuyTokens = useCallback(() => {
    openPricingModal();
  }, [openPricingModal]);

  // Handle tier selection from pricing modal
  // CRITICAL FIX: Must refresh balance after purchase completes
  const handleSelectTier = useCallback(async (tierId) => {
    console.log('[QAICoachPage] Selected tier:', tierId);
    closePricingModal();

    // After purchase, refresh balance to get updated token count
    // Small delay to allow backend to process payment
    setTimeout(async () => {
      console.log('[QAICoachPage] Refreshing balance after tier purchase...');
      const result = await refreshBalance();
      if (result) {
        console.log('[QAICoachPage] Balance updated after purchase:', result.balance);
      }
    }, 1000);
  }, [closePricingModal, refreshBalance]);

  // Don't render until initialized
  // If initialized but no token, the useEffect above will redirect to login
  if (!isInitialized) {
    return (
      <Box
        sx={{
          height: '100vh',
          backgroundColor: '#0A0A0F',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Loading...</Typography>
      </Box>
    );
  }

  // If no token after initialization, show redirecting message
  // (useEffect will handle the actual redirect)
  if (!qaiToken) {
    return (
      <Box
        sx={{
          height: '100vh',
          backgroundColor: '#0A0A0F',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Redirecting to login...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // MOBILE FIX: Use 100dvh for dynamic viewport height on mobile
        // This accounts for mobile browser chrome (address bar, etc.)
        // Fallback to 100vh for older browsers
        height: { xs: '100dvh', lg: '100vh' },
        // iOS Safari fallback using -webkit-fill-available
        minHeight: { xs: '-webkit-fill-available', lg: 'auto' },
        backgroundColor: '#0A0A0F', // Pure dark like Bolt.new
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* ═══════════════════════════════════════════════════════════════════════
          HEADER - Minimal Bolt-style
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="header"
        sx={{
          backgroundColor: '#0A0A0F',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          px: { xs: 1.5, sm: 2, md: 3 },  // Tighter padding on mobile
          py: { xs: 0.75, sm: 1 },         // Reduced vertical padding on mobile
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
            gap: { xs: 1, sm: 1.5 }
          }}
        >
          {/* Left: Logo + Tagline + QBridge Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, minWidth: 0 }}>
            {/* Logo + Tagline */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, flexShrink: 0 }}>
              <QAILogo size="small" showText textVariant="short" />
              {/* Tagline - Desktop only - Prominent */}
              <Typography
                sx={{
                  display: { xs: 'none', lg: 'block' },
                  color: '#A1A1AA',
                  fontSize: { lg: '0.9375rem', xl: '1rem' },  // Slightly smaller on smaller desktops
                  fontWeight: 500,
                  fontFamily: "'Inter', -apple-system, sans-serif",
                  borderLeft: '1px solid rgba(255, 255, 255, 0.12)',
                  paddingLeft: 1.5,
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.01em'
                }}
              >
                Ask Q. Understand Your Data.
              </Typography>
            </Box>

            {/* Inline QBridge Status - Real-time MT5 Connection */}
            <Tooltip
              title={
                qbridgeStatus.connected
                  ? qbridgeStatus.isFallbackDetection
                    ? 'Connected: MT5 files accessible (EA Assistant ready)'
                    : `Connected: ${qbridgeStatus.mt5Account || 'MT5'} @ ${qbridgeStatus.mt5Broker || 'Broker'}`
                  : qbridgeStatus.qbridgeRunning
                    ? 'QBridge running - MT5 not detected. Open MT5 and login.'
                    : 'QBridge not running. Click to setup.'
              }
              placement="bottom"
            >
              <Chip
                icon={qbridgeStatus.connected ? <CheckCircleIcon /> : <ErrorIcon />}
                label={qbridgeStatus.connected ? 'MT5' : 'MT5'}
                size="small"
                onClick={handleMT5Click}
                sx={{
                  backgroundColor: 'transparent',
                  color: qbridgeStatus.connected ? '#27B56D' : '#EF4444',
                  border: `1px solid ${qbridgeStatus.connected
                    ? 'rgba(39, 181, 109, 0.25)'
                    : 'rgba(239, 68, 68, 0.25)'}`,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },  // Smaller on mobile
                  fontWeight: 600,
                  height: { xs: '26px', sm: '30px' },           // Compact on mobile
                  cursor: 'pointer',
                  '& .MuiChip-label': { px: { xs: 0.5, sm: 1 } },
                  '& .MuiChip-icon': {
                    color: qbridgeStatus.connected ? '#27B56D' : '#EF4444',
                    fontSize: { xs: '0.875rem', sm: '1rem' },   // Smaller icon on mobile
                    ml: { xs: 0.25, sm: 0.5 }
                  },
                  '&:hover': {
                    backgroundColor: qbridgeStatus.connected
                      ? 'rgba(39, 181, 109, 0.1)'
                      : 'rgba(239, 68, 68, 0.1)'
                  }
                }}
              />
            </Tooltip>
          </Box>

          {/* ═══════════════════════════════════════════════════════════════════════
              CENTER - Tab Navigation (Phase 5 - EA Assistant)
              ═══════════════════════════════════════════════════════════════════════ */}
          {FEATURE_FLAGS.EA_ASSISTANT_TAB && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: '36px',
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#27B56D',
                    height: '3px',
                    borderRadius: '3px 3px 0 0'
                  },
                  '& .MuiTab-root': {
                    minHeight: '36px',
                    minWidth: 'auto',
                    padding: '6px 16px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'rgba(255, 255, 255, 0.6)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      backgroundColor: 'rgba(255, 255, 255, 0.04)'
                    },
                    '&.Mui-selected': {
                      color: '#FFFFFF'
                    }
                  }
                }}
              >
                <Tab
                  value="chat"
                  icon={<ChatIcon sx={{ fontSize: '1.125rem' }} />}
                  iconPosition="start"
                  label="Ask Q"
                  sx={{ gap: 0.75 }}
                />
                <Tab
                  value="my-eas"
                  icon={<CodeIcon sx={{ fontSize: '1.125rem' }} />}
                  iconPosition="start"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      My EAs
                      {/* Attention badge - shows "NEW" for first-time users */}
                      <Chip
                        label="NEW"
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          backgroundColor: 'rgba(124, 58, 237, 0.2)',
                          color: '#a855f7',
                          border: '1px solid rgba(124, 58, 237, 0.3)',
                          '& .MuiChip-label': { px: 0.75 }
                        }}
                      />
                    </Box>
                  }
                  sx={{ gap: 0.75 }}
                />
              </Tabs>
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 }, flexShrink: 0 }}>
            {/* Token Balance + Add Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 } }}>
              <TokenBalance
                balance={balance}
                isLow={isLowBalance}
                onBuyTokens={handleBuyTokens}
                size="small"
              />
              {/* +Add Token Button - Highlighted for payment */}
              <Tooltip title="Add Tokens" arrow>
                <IconButton
                  onClick={handleBuyTokens}
                  size="small"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    borderRadius: '6px',
                    padding: { xs: '4px 6px', sm: '4px 10px' },
                    minWidth: { xs: 36, sm: 'auto' },    // Adequate touch target
                    minHeight: { xs: 36, sm: 'auto' },
                    backgroundColor: 'rgba(39, 181, 109, 0.12)',
                    border: '1px solid rgba(39, 181, 109, 0.3)',
                    color: '#27B56D',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(39, 181, 109, 0.2)',
                      borderColor: 'rgba(39, 181, 109, 0.5)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(39, 181, 109, 0.25)'
                    }
                  }}
                >
                  <AddIcon sx={{ fontSize: { xs: '1.125rem', sm: '1rem' } }} />
                  <Typography
                    sx={{
                      display: { xs: 'none', sm: 'block' }, // Hide on mobile, show on sm+
                      fontSize: '0.875rem', // MOBILE FIX: 14px minimum
                      fontWeight: 600,
                      fontFamily: "'Inter', -apple-system, sans-serif"
                    }}
                  >
                    Add
                  </Typography>
                </IconButton>
              </Tooltip>
            </Box>

            {/* Help - 44px touch target on mobile for WCAG compliance */}
            <Tooltip title="Help" arrow>
              <IconButton
                onClick={() => setShowHelpModal(true)}
                size="small"
                sx={{
                  color: '#71717A',
                  width: { xs: 44, md: 32 },
                  height: { xs: 44, md: 32 },
                  minWidth: { xs: 44, md: 32 },
                  minHeight: { xs: 44, md: 32 },
                  '&:hover': {
                    color: '#7c3aed',
                    backgroundColor: 'rgba(124, 58, 237, 0.08)'
                  }
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: '1.125rem' }} />
              </IconButton>
            </Tooltip>

            {/* Settings - 44px touch target on mobile */}
            <Tooltip title="Settings" arrow>
              <IconButton
                size="small"
                sx={{
                  color: '#71717A',
                  width: { xs: 44, md: 32 },
                  height: { xs: 44, md: 32 },
                  minWidth: { xs: 44, md: 32 },
                  minHeight: { xs: 44, md: 32 },
                  '&:hover': {
                    color: '#27B56D',
                    backgroundColor: 'rgba(39, 181, 109, 0.08)'
                  }
                }}
              >
                <SettingsIcon sx={{ fontSize: '1.125rem' }} />
              </IconButton>
            </Tooltip>

            {/* Logout - 44px touch target on mobile */}
            <Tooltip title="Logout" arrow>
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  color: '#71717A',
                  width: { xs: 44, md: 32 },
                  height: { xs: 44, md: 32 },
                  minWidth: { xs: 44, md: 32 },
                  minHeight: { xs: 44, md: 32 },
                  '&:hover': {
                    color: '#EF4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.08)'
                  }
                }}
              >
                <LogoutIcon sx={{ fontSize: '1.125rem' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════════════════════════════════════
          MOBILE TAB BAR (Phase 5 - EA Assistant)
          Shows on mobile/tablet when header tabs are hidden
          ═══════════════════════════════════════════════════════════════════════ */}
      {FEATURE_FLAGS.EA_ASSISTANT_TAB && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            backgroundColor: '#0A0A0F',
            px: 1
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: '44px',
              width: '100%',
              '& .MuiTabs-indicator': {
                backgroundColor: '#27B56D',
                height: '3px',
                borderRadius: '3px 3px 0 0'
              },
              '& .MuiTab-root': {
                minHeight: '44px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem', // MOBILE FIX: 16px for better readability
                color: 'rgba(255, 255, 255, 0.6)',
                '&.Mui-selected': {
                  color: '#FFFFFF'
                }
              }
            }}
          >
            <Tab
              value="chat"
              icon={<ChatIcon sx={{ fontSize: '1.125rem' }} />}
              iconPosition="start"
              label="Ask Q"
              sx={{ gap: 0.75 }}
            />
            <Tab
              value="my-eas"
              icon={<CodeIcon sx={{ fontSize: '1.125rem' }} />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  My EAs
                  <Chip
                    label="NEW"
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      backgroundColor: 'rgba(124, 58, 237, 0.2)',
                      color: '#a855f7',
                      '& .MuiChip-label': { px: 0.5 }
                    }}
                  />
                </Box>
              }
              sx={{ gap: 0.75 }}
            />
          </Tabs>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          MAIN CONTENT - PHASE 2.1: 3-Column CSS Grid Layout
          Desktop (1280px+): [180px Watchlist | 1fr DNA Matrix | 280px Chat]
          Mobile/Tablet: Single column with FAB toggle
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          // MOBILE FIX: Ensure minHeight: 0 for proper flex sizing
          minHeight: 0,
          // PHASE 2.1: CSS Grid at lg breakpoint, flex for mobile
          display: { xs: 'flex', lg: 'grid' },
          flexDirection: { xs: 'column', lg: 'unset' },
          // PHASE 2.1: 3-column grid [180px | 1fr | 280px] - uses tokens.css variables
          gridTemplateColumns: {
            lg: isDashboardCollapsed
              ? '0px 1fr 280px'  // Collapsed: hide watchlist, expand center
              : '180px 1fr 280px'  // Normal: fixed watchlist, flexible center, fixed chat
          },
          // Keep overflow hidden to prevent page-level scrolling
          overflow: 'hidden',
          px: { xs: 0, sm: 0.5, md: 1, lg: '8px' },  // PHASE 2.1: 8px gap as padding
          py: { xs: 0, md: 1 },
          gap: { xs: 0, lg: '8px' },  // PHASE 2.1: 8px grid gap
          position: 'relative'
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════
            LEFT PANEL - Watchlist (PHASE 2.1: Fixed 180px)
            Desktop: QSignalList symbol selector - fixed 180px width
            Mobile: Hidden (dashboard FAB shows full signal board instead)
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            // PHASE 2.1: Fixed 180px width - explicit constraints for strict enforcement
            width: { lg: '180px' },
            minWidth: { lg: '180px' },
            maxWidth: { lg: '180px' },
            minHeight: 0,
            display: { xs: 'none', lg: 'flex' },  // Hide on mobile, grid handles on desktop
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#12121a',  // var(--bg-surface)
            borderRadius: '8px',
            border: '1px solid #2a2a3a',  // var(--border-default)
            transition: 'all 0.3s ease',
            // PHASE 2.1: Handle collapsed state
            opacity: isDashboardCollapsed ? 0 : 1,
            visibility: isDashboardCollapsed ? 'hidden' : 'visible',
            ...(isDashboardCollapsed && {
              width: 0,
              minWidth: 0,
              maxWidth: 0,
              overflow: 'hidden'
            })
          }}
        >
          <QSignalList
            signals={signalTableData}
            selectedSymbol={selectedSymbol}
            onSymbolSelect={setSelectedSymbol}
            onAskQ={(promptOrSignal) => {
              // FIX: Always use selectedQuantXSymbol for chat context
              // QSignalList passes STRING prompt with cross-pair, but Q.AI needs QuantX symbol
              // Convert any prompt to use the currently selected QuantX symbol
              const symbolToUse = selectedQuantXSymbol || 'XAUUSD_ct';
              const message = typeof promptOrSignal === 'string'
                ? promptOrSignal.replace(/Analyze\s+\S+/i, `Analyze ${symbolToUse}`)
                : `Tell me more about ${symbolToUse} signal`;
              console.log('[QAICoachPage] Ask Q from Signal List:', { original: promptOrSignal, modified: message, selectedQuantXSymbol });
              sendMessage(message, 'chat');
            }}
            availableSymbols={dropdownSymbols}
            showSearch={true}
            showCategories={false}
          />
        </Box>

        {/* ═══════════════════════════════════════════════════════════════════════
            CENTER PANEL - DNA Matrix (PHASE 2.1: Flexible 1fr)
            Desktop: QSignalBoard with expanded signal grid - takes remaining space
            Mobile: Full-screen when dashboard view active
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            // PHASE 2.1: Flexible 1fr - takes remaining space after 180px + 280px
            flex: { xs: 1, lg: 1 },  // flex: 1 for grid item to fill 1fr column
            minWidth: 0,  // Critical: Allow shrink in grid context
            minHeight: 0,
            display: FEATURE_FLAGS.MOBILE_RESPONSIVE && isMobile
              ? (mobileView === 'dashboard' ? 'flex' : 'none')
              : 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            order: { xs: 2, lg: 'unset' },
            transition: 'all 0.3s ease'
          }}
        >
          <QSignalBoard
            isLive={isAnalysisLive}
            lastUpdated={lastAnalysisUpdate}
            showViewToggle={false}  /* Phase 2: Hide toggle for cleaner institutional UI */
            viewMode={signalViewMode}
            onViewModeChange={(event, newMode) => newMode && setSignalViewMode(newMode)}
            showSymbolSelector={FEATURE_FLAGS.ENHANCED_SIGNAL_GRID}
            selectedSymbol={selectedSymbol}
            onSymbolChange={setSelectedSymbol}
            // Phase 2B Fix: Show PAIR symbols (GOLD/SILVER) in dropdown, not QuantX symbols
            availableSymbols={dropdownSymbols.length > 0 ? dropdownSymbols : undefined}
            // Phase 1B: Pass price context from QuantX data
            priceData={FEATURE_FLAGS.ENHANCED_SIGNAL_GRID && quantXData?.currentState ? {
              price: quantXData.currentState.price,
              change: quantXData.currentState.change,
              changePercent: quantXData.currentState.changePercent
            } : null}
          >
            {/* Loading Progress - Shows in left panel during AI processing */}
            {isLoading && progressState && (
              <QAILoadingProgress
                isLoading={isLoading}
                currentStep={progressState.currentStep}
                progress={progressState.progress}
                statusMessage={progressState.message}
                warnings={warnings}
                compact={false}
              />
            )}

            {/* P&L Summary - Hidden until API provides data */}
            {/* TODO: Uncomment when P&L data is available from API
            <QPnLSummary
              totalPnL={null}
              todayPnL={null}
              strategies={null}
            />
            */}

            {/* Signal View - Grid or Table based on toggle (Phase 1 MT5-style polish) */}
            {FEATURE_FLAGS.SIGNAL_GRID_VIEW && signalViewMode === 'grid' ? (
              <QSignalGrid
                signals={signalTableData}
                onAskQ={(signal) => {
                  // FIX: Use selectedQuantXSymbol (mapped from selected symbol), not signal.symbol (cross-pair)
                  const symbolToUse = selectedQuantXSymbol || 'XAUUSD_ct';
                  console.log('[QAICoachPage] Ask Q from Grid:', { signalSymbol: signal?.symbol, selectedQuantXSymbol, symbolToUse });
                  sendMessage(`Tell me more about ${symbolToUse} signal`, 'chat');
                }}
                showViewToggle={false}
                showCategories={true}
                // Phase 1B/2: Enhanced Signal Grid data from QuantX API + Strategy DNA
                quantXData={FEATURE_FLAGS.ENHANCED_SIGNAL_GRID ? mergedQuantXData : null}
                selectedSymbol={selectedSymbol}
                // FIX: Pass availableSymbols to show all symbols in category counts
                availableSymbols={dropdownSymbols}
              />
            ) : (
              <QSignalTable
                signals={signalTableData}
                onAskQ={(signal) => {
                  // FIX: Use selectedQuantXSymbol, not signal.symbol
                  const symbolToUse = selectedQuantXSymbol || 'XAUUSD_ct';
                  sendMessage(`Tell me more about ${symbolToUse} signal`, 'chat');
                }}
              />
            )}
          </QSignalBoard>
        </Box>

        {/* Dashboard Collapse Toggle Button (Desktop Only) - PHASE 2.1 Updated */}
        {FEATURE_FLAGS.COLLAPSIBLE_DASHBOARD && !isMobile && (
          <Tooltip
            title={isDashboardCollapsed ? 'Show Panels' : 'Collapse Panels'}
            placement="right"
          >
            <IconButton
              onClick={() => setIsDashboardCollapsed(!isDashboardCollapsed)}
              sx={{
                position: 'absolute',
                // PHASE 2.1: Position at edge of center panel (after 180px watchlist + 8px gap)
                left: isDashboardCollapsed ? 8 : 'calc(180px + 8px)',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: 24,
                height: 40,
                borderRadius: '0 6px 6px 0',
                backgroundColor: 'rgba(39, 181, 109, 0.1)',
                border: '1px solid rgba(39, 181, 109, 0.2)',
                color: '#27B56D',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(39, 181, 109, 0.2)',
                  borderColor: 'rgba(39, 181, 109, 0.4)'
                }
              }}
            >
              {isDashboardCollapsed ? <ChevronRightIcon sx={{ fontSize: 18 }} /> : <ChevronLeftIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        )}

        {/* ═══════════════════════════════════════════════════════════════════════
            RIGHT PANEL - Chat (PHASE 2.1: Fixed 280px)
            Desktop: QChatWindow - fixed 280px width from grid
            Mobile: Full-screen when chat view active
            Phase 5: Also supports EA Manager tab
            ═══════════════════════════════════════════════════════════════════════ */}
        <Box
          sx={{
            // PHASE 2.1: Fixed 280px width - explicit constraints for strict enforcement
            width: { lg: '280px' },
            minWidth: { lg: '280px' },
            maxWidth: { lg: '280px' },
            // Mobile: full width flex
            flex: { xs: '1 1 auto', lg: 'none' },
            // CRITICAL: Use minHeight: 0 to allow flex children to shrink properly
            minHeight: 0,
            // MOBILE FIX: Ensure full height on mobile
            height: { xs: '100%', lg: 'auto' },
            display: FEATURE_FLAGS.MOBILE_RESPONSIVE && isMobile
              ? (mobileView === 'chat' ? 'flex' : 'none')
              : 'flex',
            flexDirection: 'column',
            // CRITICAL: Allow internal scrolling, don't clip the input area
            overflow: 'hidden',
            order: { xs: 1, lg: 'unset' },
            borderRadius: { xs: 0, lg: '8px' },
            border: { xs: 'none', lg: '1px solid #2a2a3a' },  // var(--border-default)
            backgroundColor: '#0a0a0f',  // var(--bg-primary)
            transition: 'all 0.3s ease'
          }}
        >
          {/* ─────────────────────────────────────────────────────────────────────
              CHAT VIEW (Ask Q Tab)
              ───────────────────────────────────────────────────────────────────── */}
          {activeTab === 'chat' && (
            <QChatWindow
              messages={messages}
              balance={balance}
              isLoading={isLoading}
              isLowBalance={isLowBalance}
              canSend={canSend}
              onSendMessage={sendMessage}
              onActionCard={handleActionCard}
              onActionChip={handleActionChip}
              onCancelStream={cancelStream}
              onBuyTokens={handleBuyTokens}
              onClearChat={clearMessages}
              error={error}
              // Task 2.3: Conversation continuity indicator
              hasConversation={hasConversation}
              // Task 4.6: Conversation history
              conversationId={conversationId}
              conversationHistory={conversationHistory}
              isLoadingHistory={isLoadingHistory}
              onSelectConversation={loadConversation}
              onDeleteConversation={deleteConversation}
              onRefreshHistory={refreshConversationHistory}
              // UI-6/8: Progress and warnings
              progressState={progressState}
              warnings={warnings}
            />
          )}

          {/* ─────────────────────────────────────────────────────────────────────
              EA MANAGER VIEW (My EAs Tab) - Phase 5
              Same expandable space as Chat, institutional-grade layout
              ───────────────────────────────────────────────────────────────────── */}
          {FEATURE_FLAGS.EA_ASSISTANT_TAB && activeTab === 'my-eas' && (
            <EAManagerTab
              token={qaiToken}
              balance={balance}
              onAskQ={handleAskQFromEA}
              onBuyTokens={handleBuyTokens}
            />
          )}
        </Box>

        {/* ═══════════════════════════════════════════════════════════════════════
            MOBILE FAB - Toggle between Chat and Dashboard (Feature Flagged)
            FIXED: Positioned INSIDE the messages area, not over input
            ═══════════════════════════════════════════════════════════════════════ */}
        {FEATURE_FLAGS.MOBILE_RESPONSIVE && isMobile && (
          <Fab
            onClick={() => setMobileView(mobileView === 'chat' ? 'dashboard' : 'chat')}
            sx={{
              position: 'fixed',
              // FIXED: Position higher - above the input area (input ~74px + 16px gap + safe area)
              bottom: 'calc(95px + env(safe-area-inset-bottom, 0px))',
              right: 12,
              zIndex: 1000,
              width: 44,     // Smaller to be less intrusive
              height: 44,
              minHeight: 44, // Override MUI default
              backgroundColor: '#27B56D',
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(39, 181, 109, 0.35)',
              '&:hover': {
                backgroundColor: '#22A55E'
              }
            }}
          >
            {mobileView === 'chat' ? <DashboardIcon /> : <ChatIcon />}
          </Fab>
        )}
      </Box>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricingModal}
        onClose={closePricingModal}
        onSelectTier={handleSelectTier}
        currentBalance={balance}
        token={qaiToken}
      />

      {/* QBridge Setup Modal (Legacy - kept for backwards compatibility) */}
      <QBridgeSetupModal
        open={showSetupModal}
        onClose={() => setShowSetupModal(false)}
        onSkip={() => setShowSetupModal(false)}
        onComplete={handleSetupComplete}
        userEmail={qaiUser?.email || ''}
      />

      {/* QBridge Wizard V2 (Auto-detection, auto-start, prominent terms) */}
      <QBridgeSetupWizardV2
        open={showQBridgeWizard}
        onClose={() => setShowQBridgeWizard(false)}
        onComplete={(importedCount) => {
          setShowQBridgeWizard(false);
          handleSetupComplete();
        }}
      />

      {/* QBridge Help Modal */}
      <QBridgeHelpModal
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        onAskHelp={handleAskQBridgeHelp}
      />

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER - QFORGEX Credit
          Hidden on mobile to maximize chat space and prevent input overlap
          FONT FIX: Consistent 0.875rem (14px) typography with improved contrast
          ═══════════════════════════════════════════════════════════════════════ */}
      <Box
        component="footer"
        sx={{
          display: { xs: 'none', md: 'block' },  // Hide on mobile/tablet
          backgroundColor: '#0A0A0F',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          py: 1.25,
          px: 2,
          textAlign: 'center',
          flexShrink: 0
        }}
      >
        <Typography
          sx={{
            color: '#9CA3AF', // FONT FIX: Brighter color for better readability
            fontSize: '0.875rem', // 14px - institutional minimum readable
            fontWeight: 500, // FONT FIX: Slightly bolder for readability
            fontFamily: "'Inter', -apple-system, sans-serif",
            letterSpacing: '0.01em'
          }}
        >
          Powered by{' '}
          <Box
            component="span"
            sx={{
              color: '#F0F0F5', // FONT FIX: Brighter accent for brand name
              fontWeight: 600
            }}
          >
            QFORGEX
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default QAICoachPage;
